import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@nexus/ui";
import { useTranslation } from "react-i18next";

import {
  EXTEND_DUE_PRESET_DAYS,
  extendDueYmd,
} from "../../lib/order-deadline";

/** Grows the touch target to 44px tall without growing the visible chip. */
const HIT_AREA_CLASS = {
  xs: "relative before:absolute before:inset-x-0 before:-inset-y-2.5 before:content-['']",
  sm: "relative before:absolute before:inset-x-0 before:-inset-y-1.5 before:content-['']",
} as const;

interface ExtendDueMenuProps {
  dueYmd: string;
  todayYmd: string;
  pending?: boolean;
  /** Dense rows use the 24px trigger; the detail panel uses 32px. */
  size?: "xs" | "sm";
  onExtend: (nextDueYmd: string) => void;
}

/**
 * Quick "snooze" for an overdue deadline. Clicks are contained so the menu
 * can sit inside a clickable row without also opening the row's panel —
 * portalled menu items still bubble through the React tree.
 */
export function ExtendDueMenu({
  dueYmd,
  todayYmd,
  pending = false,
  size = "sm",
  onExtend,
}: ExtendDueMenuProps) {
  const { t } = useTranslation();

  return (
    <span
      className="inline-flex shrink-0"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size={size}
            iconStart="clock"
            className={HIT_AREA_CLASS[size]}
            disabled={pending}
            aria-label={t("marketplace.orders.extend.triggerAria", "Extend due date")}
          >
            {t("marketplace.orders.extend.trigger", "Extend")}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-32">
          {EXTEND_DUE_PRESET_DAYS.map((days) => (
            <DropdownMenuItem
              key={days}
              className="text-xs"
              onSelect={() => {
                const next = extendDueYmd(dueYmd, todayYmd, days);
                if (next) onExtend(next);
              }}
            >
              {t("marketplace.orders.extend.preset", { count: days })}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </span>
  );
}
